import { useCallback } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from '@/hooks/useTranslation';
import { signalsFetchSeries } from '@/features/signals/api/SignalsApiClient';
import { signalErrorMessage } from '@/features/machine-learning/hooks/useMLInferenceJob';
import type { SignalsSeriesPayload, SignalsQuery } from '@/features/signals/type/SignalsTypes';
import type { BaseResponse } from '@/lib/http/HttpClient';

type MLSignalResponse = BaseResponse & SignalsSeriesPayload;

/**
 * Thin wrapper around the signals feature's fetch: the last passes of the machine's newest
 * part, the four signals the live model reads.
 */
export function useMLSignal(query: SignalsQuery | null) {
  const http = useApiClient();
  const { t } = useTranslation();

  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      try {
        const payload = await signalsFetchSeries(query!, http ?? undefined);
        return {
          ok: true as const,
          data: { timestamp: new Date().toISOString(), ...payload },
        };
      } catch (err) {
        const message = signalErrorMessage(err, t);
        if (message !== null) throw new Error(message, { cause: err });
        throw err;
      }
    },
    [query, http, t]
  );

  const { data, isLoading, error, retry } = useApiCall<MLSignalResponse>({
    apiFn,
    immediate: query !== null,
    deps: [query],
  });

  return { data, isLoading, error, retry };
}
